import { jsx, jsxs } from 'react/jsx-runtime';
import { useContext } from 'react';
import { FlagsContext } from '@sketch/modules-common';
import { clampCurrentBreakpoint } from '../../utils/clampCurrentBreakpoint.js';
import { convertSize } from './convertSize.js';
import { useCurrentBreakpoint, Grid } from '@sketch/components';
import './components/Item/index.js';
import './hooks/index.js';
import '../Hero/index.js';
import { StyledNavigationSelector, Separator, GenericTitle, Container, ItemsContainer, StyledGrid } from './Section.styles.js';
import '../../hooks/index.js';
import { useIsTitleSticky } from './hooks/useIsTitleSticky.js';
import { useHero } from '../Hero/useHero.js';
import { useTrackEvent } from '../../hooks/useTrackEvent.js';
import { useSectionFilter } from './hooks/useSectionFilter.js';
import Items from './components/Item/Item.js';

// Helpers
// Filter sections without items
const isSectionWithItems = ({ items, dismissedHeroes, }) => {
    if (!items)
        return;
    const filteredItems = items.filter(item => {
        if (item.__typename === 'Item' &&
            item.type === 'HERO' &&
            dismissedHeroes.includes(item.id)) {
            return false;
        }
        return true;
    });
    return filteredItems.length > 0;
};
/**
 * Section
 *
 * Renders each section on the Discover page
 *
 * Sections have 3 types
 * - Generic : A section with titles and buttons
 * - Featured : A blank section. No title or buttons
 * - Separator : A line, separating sections
 */
const Section = ({ id: sectionId, type, size, title, subTitle, items, links, visibility, categories, limit, userPermissions, stretchedContent, context, itemComponent: ItemComponent = Items, }) => {
    var _a, _b, _c, _d;
    const flags = useContext(FlagsContext);
    const { ref: titleContainerRef, isTitleContainerSticky } = useIsTitleSticky();
    // Hero actions
    const { dismissedHeroes } = useHero();
    // Analytics
    const { trackEvent } = useTrackEvent();
    // Breakpoint conditions
    const currentBreakpoint = useCurrentBreakpoint();
    const clampedBreakpoint = clampCurrentBreakpoint(currentBreakpoint);
    // Apply filters to the Section items
    const { items: filterFeatured, sectionFilter, setSectionFilter, } = useSectionFilter({
        selectedFilter: (_b = (_a = categories === null || categories === void 0 ? void 0 : categories[0]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '',
        userPermissions,
        items: items || [],
    });
    const isHiddenForCurrentBreakpoint = (_c = visibility === null || visibility === void 0 ? void 0 : visibility.hideOnBreakpoint) === null || _c === void 0 ? void 0 : _c.includes(clampedBreakpoint);
    const isSectionHiddenForFF = Object.keys(flags).some(flagName => {
        var _a;
        return (flags[flagName] === true && ((_a = visibility === null || visibility === void 0 ? void 0 : visibility.hiddenForFF) === null || _a === void 0 ? void 0 : _a.includes(flagName)));
    });
    // Don't render sections without items
    if (type !== 'separator' &&
        !isSectionWithItems({
            items: [...filterFeatured.featured, ...filterFeatured.nonFeatured],
            dismissedHeroes,
        })) {
        return null;
    }
    // We return nothing if this section should be hidden due to a FF
    if (isSectionHiddenForFF) {
        return null;
    }
    if (isHiddenForCurrentBreakpoint) {
        return null;
    }
    const handleFilterSelection = ({ value, label, }) => {
        trackEvent(`${context} - Section`, {
            type: 'click',
            target: label,
            section: title,
        });
        setSectionFilter(value);
    };
    const getSubtitleContent = () => {
        var _a, _b, _c;
        let content;
        if (subTitle) {
            if ((_a = subTitle.visibility) === null || _a === void 0 ? void 0 : _a.hideOnBreakpoint) {
                const isHiddenForCurrentBreakpoint = (_c = (_b = subTitle.visibility) === null || _b === void 0 ? void 0 : _b.hideOnBreakpoint) === null || _c === void 0 ? void 0 : _c.includes(clampedBreakpoint);
                content = isHiddenForCurrentBreakpoint ? '' : subTitle.text;
            }
            else {
                content = subTitle.text;
            }
        }
        return content;
    };
    const renderCategoriesButtons = () => {
        const items = categories === null || categories === void 0 ? void 0 : categories.map(category => ({
            type: 'text',
            label: category.name,
            value: category.id,
        }));
        if (!(items === null || items === void 0 ? void 0 : items.length)) {
            return;
        }
        return (jsx(StyledNavigationSelector, { name: `${sectionId}-categories`, items: items, onChange: handleFilterSelection, selected: sectionFilter, hideTooltip: true }));
    };
    const renderTitles = () => {
        switch (type) {
            case 'generic': {
                // Fix the internal type of link
                const titleLink = (links === null || links === void 0 ? void 0 : links[0])
                    ? { url: links[0].url, text: links[0].text || '' }
                    : undefined;
                return (jsx(GenericTitle, { ref: titleContainerRef, title: title || '', titleLink: titleLink, subTitle: getSubtitleContent(), categories: renderCategoriesButtons(), "$isSticky": isTitleContainerSticky }));
            }
            case 'separator': {
                return (jsx(Grid.Column, Object.assign({}, convertSize(size), { children: jsx(Separator, {}) })));
            }
            // Featured sections don't have titles so we skip this
            case 'featured': {
                return;
            }
        }
    };
    // Order items, where featured are first and keep just one featured item
    const orderedItems = [
        ...(((_d = filterFeatured === null || filterFeatured === void 0 ? void 0 : filterFeatured.featured) === null || _d === void 0 ? void 0 : _d.slice(0, 1)) || []),
        ...((filterFeatured === null || filterFeatured === void 0 ? void 0 : filterFeatured.nonFeatured) || []),
    ]
        // Apply limit of items
        // If the value is null, we need to pass undefined, otherwise will return an
        // empty array
        .slice(0, limit === null ? undefined : limit);
    const hasItems = !!orderedItems.length;
    return (jsx(Grid.Column, Object.assign({}, convertSize(size), { children: jsxs(Container, { children: [renderTitles(), hasItems && (jsx(ItemsContainer, { children: jsx(StyledGrid, Object.assign({ "$stretch": Boolean(stretchedContent), fullHeight: true, 
                        // increasing the xl gutter size
                        gutter: { xl: 48, lg: 20, md: 20, sm: 20, xs: 16 } }, { children: orderedItems.map(item => (jsx(ItemComponent, { "data-testid": item.id, item: item, context: context }, item.id))) })) }))] }, sectionId) })));
};

export { Section as default };
